import React, { useState } from 'react';
import { contacts } from '../mock';
import { Search, Filter, Plus, Mail, Phone, Grid3x3, List, Star, TrendingUp } from 'lucide-react';
import { cn } from '../lib/utils';

function money(n) { return `$${n.toLocaleString()}`; }

export default function Contacts() {
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('All');
  const [view, setView] = useState('grid');

  const roles = ['All', 'Lead', 'Borrower', 'Borrowers'];
  const filtered = contacts.filter((c) => {
    const r = role === 'All' || c.role === role || (role === 'Borrower' && c.role === 'Borrowers');
    const q = !query || c.name.toLowerCase().includes(query.toLowerCase()) || c.city.toLowerCase().includes(query.toLowerCase());
    return r && q;
  });

  return (
    <div className="max-w-[1500px] mx-auto space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] text-neutral-500">Workspace / Contacts</div>
          <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">Contacts</h1>
          <p className="text-[13px] text-neutral-500">{contacts.length} total · everyone in your Kinfolk network</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="h-9 px-3 rounded-lg border hairline bg-white hover:border-neutral-400 text-[12.5px] font-semibold text-neutral-700 flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5" /> Filters
          </button>
          <button className="h-9 px-3 rounded-lg btn-ink text-[12.5px] font-semibold flex items-center gap-1.5">
            <Plus className="h-4 w-4" /> Add contact
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or city…"
            className="w-full h-10 pl-9 pr-3 rounded-lg bg-white border hairline text-[13px] placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900"
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg border hairline p-0.5 bg-white">
          {roles.map((r) => (
            <button key={r} onClick={() => setRole(r)}
              className={cn('text-[11.5px] font-semibold px-3 py-1.5 rounded-md transition',
                role === r ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900')}>
              {r}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-1 rounded-lg border hairline p-0.5 bg-white">
          <button onClick={() => setView('grid')} className={cn('h-7 w-7 rounded-md flex items-center justify-center', view === 'grid' ? 'bg-neutral-900 text-white' : 'text-neutral-500')}>
            <Grid3x3 className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => setView('list')} className={cn('h-7 w-7 rounded-md flex items-center justify-center', view === 'list' ? 'bg-neutral-900 text-white' : 'text-neutral-500')}>
            <List className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Grid */}
      {view === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => <ContactCard key={c.id} c={c} />)}
        </div>
      )}

      {view === 'list' && (
        <div className="rounded-2xl border hairline bg-white overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-neutral-500 font-bold border-b hairline bg-neutral-50/60">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Stage</div>
            <div className="col-span-2">City</div>
            <div className="col-span-2">Source</div>
            <div className="col-span-1">Amount</div>
            <div className="col-span-1">Score</div>
            <div className="col-span-1 text-right">Touch</div>
          </div>
          <div className="divide-y divide-neutral-100">
            {filtered.map((c) => (
              <div key={c.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-neutral-50/60">
                <div className="col-span-3 flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-neutral-900 text-white font-bold text-[11px] flex items-center justify-center">{c.initials}</span>
                  <div>
                    <div className="text-[13px] font-bold text-neutral-900">{c.name}</div>
                    <div className="text-[10.5px] text-neutral-500">{c.email}</div>
                  </div>
                </div>
                <div className="col-span-2"><StageChip stage={c.stage} /></div>
                <div className="col-span-2 text-[12px] text-neutral-700">{c.city}</div>
                <div className="col-span-2 text-[12px] text-neutral-700">{c.source}</div>
                <div className="col-span-1 mono text-[12px] font-bold text-neutral-900">${(c.loanAmount / 1000).toFixed(0)}K</div>
                <div className="col-span-1 flex items-center gap-1"><Star className="h-3 w-3 text-[hsl(var(--kf-gold-deep))]" /><span className="mono text-[11.5px] font-bold">{c.score}</span></div>
                <div className="col-span-1 text-right mono text-[10.5px] text-neutral-500">{c.lastTouch}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactCard({ c }) {
  return (
    <div className="rounded-2xl border hairline bg-white p-5 card-lift group relative overflow-hidden">
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[hsl(var(--kf-gold))]/[0.04] opacity-0 group-hover:opacity-100 transition" />
      <div className="relative">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full bg-neutral-900 text-white font-bold text-[13px] flex items-center justify-center">
              {c.initials}
            </div>
            <div>
              <div className="text-[14px] font-bold text-neutral-900 leading-tight">{c.name}</div>
              <div className="text-[11px] text-neutral-500">{c.role} · {c.city}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="flex items-center justify-end gap-1 text-[10px] text-neutral-500">
              <TrendingUp className="h-3 w-3 text-[hsl(var(--kf-gold-deep))]" />
              <span className="mono font-bold text-neutral-800">{c.score}</span>
            </div>
            <div className="mono text-[10.5px] text-neutral-500 mt-0.5">score</div>
          </div>
        </div>

        <div className="mt-4 rounded-lg border hairline bg-neutral-50/50 p-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">Loan amount</div>
            <div className="mono text-[16px] font-bold text-neutral-900 mt-0.5">${c.loanAmount.toLocaleString()}</div>
          </div>
          <StageChip stage={c.stage} />
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px] text-neutral-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> Email</span>
            <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> Call</span>
          </div>
          <span className="mono">Last: {c.lastTouch}</span>
        </div>
      </div>
    </div>
  );
}

function StageChip({ stage }) {
  const tone = stage === 'Funded' || stage === 'Closing' ? 'emerald'
    : stage.includes('Underwriting') || stage.includes('Approval') || stage.includes('CD') ? 'gold'
    : 'slate';
  const cls = tone === 'emerald' ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
    : tone === 'gold' ? 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold)/0.3)]'
    : 'bg-neutral-100 text-neutral-700 border-neutral-200';
  return <span className={`inline-flex text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-md border ${cls}`}>{stage}</span>;
}
